import React, { useState } from 'react';
import { useStyletron } from 'baseui';
import { Button, KIND as BUTTON_KIND, SIZE as BUTTON_SIZE } from 'baseui/button';
import { Radio } from 'baseui/radio';
import { Textarea } from 'baseui/textarea';
import { ParagraphSmall } from 'baseui/typography';
export const PROMPT_MODE = ({
  CLASS: 'class',
  CUSTOM: 'custom'
} as const);
type PromptMode = (typeof PROMPT_MODE)[keyof typeof PROMPT_MODE];
export const AUTO_DETECT_MODAL_PHASE = ({
  PROMPT: 'prompt',
  RESULTS: 'results'
} as const);
export type AutoDetectModalPhaseT = (typeof AUTO_DETECT_MODAL_PHASE)[keyof typeof AUTO_DETECT_MODAL_PHASE];
export type AutoDetectSegmentationPanelProps = {
  isLoading: boolean;
  selectedClassName: string;
  resultCount: number;
  modalPhase: AutoDetectModalPhaseT;
  onPhaseChange: (phase: AutoDetectModalPhaseT) => void;
  onClose: () => void;
  onDetect: (textPrompt: string, textPromptMode: PromptMode) => Promise<number>;
  onSubmitAll: () => void;
  onCancelAll: () => void;
};
const radioOverrides = {
  Root: {
    style: {
      margin: 0
    }
  },
  RadioMarkOuter: {
    style: ({
      $theme
    }) => ({
      margin: 0,
      width: $theme.sizing.scale600,
      height: $theme.sizing.scale600
    })
  }
};
type RadioOptionProps = {
  checked: boolean;
  onSelect: () => void;
  children: React.ReactNode;
};
const RadioOption = ({
  checked,
  onSelect,
  children
}: RadioOptionProps) => {
  const [css, theme] = useStyletron();
  return <div className={css({
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    margin: 0,
    gap: theme.sizing.scale400
  })} onClick={onSelect}>
      <Radio checked={checked} onChange={onSelect} overrides={radioOverrides} />
      <div className={css({
      ...theme.typography.ParagraphSmall
    })}>{children}</div>
    </div>;
};
export const AutoDetectSegmentationPanel = ({
  isLoading,
  selectedClassName,
  resultCount,
  modalPhase,
  onPhaseChange,
  onClose,
  onDetect,
  onSubmitAll,
  onCancelAll
}: AutoDetectSegmentationPanelProps) => {
  const [promptMode, setPromptMode] = useState<PromptMode>(PROMPT_MODE.CLASS);
  const [customPromptText, setCustomPromptText] = useState('');
  const [css, theme] = useStyletron();
  const resetState = () => {
    onPhaseChange(AUTO_DETECT_MODAL_PHASE.PROMPT);
    setPromptMode(PROMPT_MODE.CLASS);
    setCustomPromptText('');
  };
  const handleDetect = async () => {
    const textPrompt = promptMode === PROMPT_MODE.CLASS ? selectedClassName : `${customPromptText.trim()} ${selectedClassName}`.trim();
    const count = await onDetect(textPrompt, promptMode);
    if (count > 0) {
      onPhaseChange(AUTO_DETECT_MODAL_PHASE.RESULTS);
    }
  };
  const handleClose = () => {
    resetState();
    onClose();
  };
  const handleCancelAll = () => {
    resetState();
    onCancelAll();
  };
  const handleSubmitAll = () => {
    resetState();
    onSubmitAll();
  };
  const isDetectDisabled = isLoading || promptMode === PROMPT_MODE.CUSTOM && customPromptText.trim() === '';
  return <div className={css({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.sizing.scale600,
    padding: theme.sizing.scale700,
    width: '297px',
    marginLeft: theme.sizing.scale200,
    backgroundColor: theme.colors.backgroundPrimary,
    borderRadius: theme.borders.radius400,
    boxShadow: theme.lighting.shadow600
  })}>
      <div>
        <div className={css({
        ...theme.typography.HeadingXSmall,
        margin: 0
      })}>
          Auto-detect objects
        </div>
        <div className={css({
        ...theme.typography.ParagraphXSmall,
        color: theme.colors.contentSecondary
      })}>
          Use SAM3 model to detect objects
        </div>
      </div>

      <div className={css({
      display: 'flex',
      flexDirection: 'column',
      gap: theme.sizing.scale600
    })}>
        {modalPhase === AUTO_DETECT_MODAL_PHASE.PROMPT ? <div className={css({
        display: 'flex',
        flexDirection: 'column',
        gap: theme.sizing.scale600
      })}>
            <div>
              <div className={css({
            ...theme.typography.LabelSmall,
            margin: 0,
            marginBottom: theme.sizing.scale600
          })}>
                Select a method to continue
              </div>
              <div className={css({
            display: 'flex',
            flexDirection: 'column',
            gap: theme.sizing.scale600,
            alignItems: 'flex-start',
            margin: 0
          })}>
                <RadioOption checked={promptMode === PROMPT_MODE.CLASS} onSelect={() => setPromptMode(PROMPT_MODE.CLASS)}>
                  <span className={css({
                ...theme.typography.ParagraphSmall,
                color: theme.colors.contentSecondary
              })}>
                    Detect all{' '}
                    <span className={css({
                  color: theme.colors.accent
                })}>{selectedClassName}</span>{' '}
                    objects in current frame
                  </span>
                </RadioOption>
                <RadioOption checked={promptMode === PROMPT_MODE.CUSTOM} onSelect={() => setPromptMode(PROMPT_MODE.CUSTOM)}>
                  <span className={css({
                ...theme.typography.ParagraphSmall,
                color: theme.colors.contentSecondary
              })}>
                    Enter a custom prompt
                  </span>
                </RadioOption>
              </div>
            </div>
            {promptMode === PROMPT_MODE.CUSTOM && <Textarea value={customPromptText} onChange={event => setCustomPromptText(event.currentTarget.value)} placeholder="Describe the object you want to detect" overrides={{
          Input: {
            style: {
              ...theme.typography.ParagraphSmall,
              color: theme.colors.contentSecondary
            }
          }
        }} rows={3} disabled={isLoading} />}
          </div> : <ParagraphSmall margin={0}>
            {resultCount} object{resultCount !== 1 ? 's' : ''} detected. Review on canvas.
          </ParagraphSmall>}
      </div>

      <div className={css({
      display: 'flex',
      justifyContent: 'flex-end',
      gap: theme.sizing.scale400
    })}>
        {modalPhase === AUTO_DETECT_MODAL_PHASE.PROMPT ? <>
            <Button kind={BUTTON_KIND.tertiary} size={BUTTON_SIZE.xSmall} onClick={handleClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button size={BUTTON_SIZE.xSmall} onClick={handleDetect} disabled={isDetectDisabled} isLoading={isLoading}>
              Detect objects
            </Button>
          </> : <>
            <Button kind={BUTTON_KIND.tertiary} size={BUTTON_SIZE.xSmall} onClick={handleCancelAll} disabled={isLoading}>
              Cancel All
            </Button>
            <Button size={BUTTON_SIZE.xSmall} onClick={handleSubmitAll} disabled={isLoading}>
              Submit All
            </Button>
          </>}
      </div>
    </div>;
};
export default AutoDetectSegmentationPanel;